import React, { createContext, useContext, useEffect, useState } from "react";

import { getBaseMapTileOptionFromOption, getMapTileOptionIconFromOptionType } from "../map/mapUtil";

import { useUserAuth } from "./authContext";
import { useEnv } from "./envContext";

export const MapContext = createContext();

export function useMap() {
    return useContext(MapContext);
}

export function MapProvider({ children }) {
    const [planningOperations, setPlanningOperations] = useState([]);
    const [publishedOperations, setPublishedOperations] = useState([]);
    const [constraints, setConstraints] = useState([]);
    const [alertVolumes, setAlertVolumes] = useState([]);
    const [assets, setAssets] = useState([]);
    const [coaVolumes, setCoaVolumes] = useState([]);
    const [sensors, setSensors] = useState([]);
    const [sourceData, setSourceData] = useState(new Map());
    const [colors, setColors] = useState([]);
    const [drones, setDrones] = useState([]);
    const [visibleDrones, setVisibleDrones] = useState([]);
    const [uniqueSources, setUniqueSources] = useState([]);
    const [mapTileOptions, setMapTileOptions] = useState([]);
    const [externalOperations, setExternalOperations] = useState([]);

    const { user, socket, handleFailedFetch } = useUserAuth();
    const { baseMapTileOptions } = useEnv();

    useEffect(() => {
        if (socket) {
            socket.emit("getOperations");
            socket.emit("getOperationsPlanning");
            socket.emit("getConstraints");
            socket.emit("getAlertVolumes");
            socket.emit("getAssets");
            socket.emit("getCoaVolumes");
        }
    }, [socket]);

    useEffect(() => {
        if (!socket || !user) {
            return;
        }

        const setupVolumeIds = (volumes) => {
            const volumeCount = {};
            return volumes.map((volume) => {
                const name = volume.name;
                const count = volumeCount[name] ? volumeCount[name] + 1 : 1;
                volumeCount[name] = count;
                return {
                    ...volume,
                    volumeId: `${name}-${count}`
                };
            });
        };
        socket.on("op_plan", (message) => {
            if (user.user_role_id === 0) {
                setPlanningOperations(setupVolumeIds(message));
            } else {
                setPlanningOperations(setupVolumeIds(message.filter((op) => op.organization_id === user.organization_id)));
            }
        });
        socket.on("operation", (message) => {
            const operations = setupVolumeIds(message);
            if (user.user_role_id !== 0) {
                const filteredOperations = operations.filter((operation) => {
                    return operation.organization_id === user.organization_id;
                });
                setPublishedOperations(filteredOperations);
            } else {
                setPublishedOperations(operations);
            }
        });
        socket.on("constraint", (message) => {
            const visibleConstraints = message.filter((constraint) => {
                if (user.user_role === "Operations Manager" || user.user_role === "Operator" || user.user_role === "Admin - Org") {
                    return constraint.state === "ACCEPTED";
                } else if (user.user_role === "First Responder") {
                    return constraint.created_user_id === user.id;
                } else {
                    return true;
                }
            });
            setConstraints(setupVolumeIds(visibleConstraints));
        });
        socket.on("alert_volume", (message) => {
            const visibleAlerts = message.filter((alert) => {
                if (user.user_role === "Admin") {
                    return true;
                } else if (user.user_role === "Operator") {
                    return alert.created_user_id === user.id;
                } else if (user.user_role !== "First Responder") {
                    return alert.organization_id === user.organization_id;
                } else {
                    return false;
                }
            });
            setAlertVolumes(setupVolumeIds(visibleAlerts));
        });
        socket.on("cuas_asset_volumes", (message) => {
            if (user.user_role_id === 0) {
                setAssets(setupVolumeIds(message));
            } else {
                setAssets(setupVolumeIds(message.filter((asset) => asset.organization_id === user.organization_id)));
            }
        });
        socket.on("coa_volume", (message) => {
            if (user.user_role_id === 0 || user.user_role_id === 4) {
                setCoaVolumes(setupVolumeIds(message));
            } else {
                setCoaVolumes(setupVolumeIds(message.filter((op) => op.organization_id === user.organization_id)));
            }
        });
        socket.on(`externalOperation:${user.organization_id}`, (message) => {
            setExternalOperations(message);
        });
        return () => {
            if (socket) {
                socket.off("alert_volume");
                socket.off("constraint");
                socket.off("operation");
                socket.off("op_plan");
                socket.off("cuas_asset_volumes");
                socket.off("coa_volume");
                socket.off(`externalOperation:${user.organization_id}`);
            }
        };
    }, [socket, user]);

    useEffect(() => {
        const sensorMap = new Map();
        sensors.forEach((sensor) => {
            sensorMap.set(sensor.truth_source, sensor.name);
        });
        setSourceData(sensorMap);
    }, [sensors]);

    useEffect(() => {
        if (Array.isArray(baseMapTileOptions) === false || baseMapTileOptions.length < 1) {
            const defaultTiles = [
                {
                    index: 0,
                    title: "Street (Light)",
                    url: getMapTileOptionIconFromOptionType("LIGHT"),
                    style_url: `https://tileserver-onboarding.calanalytics.com/styles/osm-light/style.json`
                },
                {
                    index: 1,
                    title: "Street (Dark)",
                    url: getMapTileOptionIconFromOptionType("DARK"),
                    style_url: `https://tileserver-onboarding.calanalytics.com/styles/osm-dark/style.json`
                }
            ];
            setMapTileOptions(defaultTiles);
        } else {
            const baseTiles = baseMapTileOptions.map((option, i) => {
                return getBaseMapTileOptionFromOption(option, i);
            });
            setMapTileOptions(baseTiles);
        }
    }, [baseMapTileOptions]);

    const fetchMapData = () => {
        fetchSensors();
        fetchColors();
        getDrones();
        getUniqueSources();
    };

    const fetchSensors = async () => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        await fetch("api/hims/sensors/get", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((data) => setSensors(data))
            .catch((err) => handleFailedFetch(err));
    };

    const fetchColors = async () => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        await fetch("api/misc/getColors", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((data) => setColors(data))
            .catch((err) => handleFailedFetch(err));
    };

    const getDrones = async () => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        await fetch("/api/drone/getAll", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((data) => {
                setDrones(data);
                const visibleDrones = data.filter((drone) => {
                    if (user.user_role_id === 0) {
                        return true;
                    } else {
                        return drone.organization_id === user.organization_id;
                    }
                });
                setVisibleDrones(visibleDrones);
            })
            .catch((err) => handleFailedFetch(err));
    };

    const getUniqueSources = async () => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        await fetch("/api/asd/getUnique", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((data) => setUniqueSources(data))
            .catch((err) => handleFailedFetch(err));
    };

    const value = {
        planningOperations,
        publishedOperations,
        constraints,
        alertVolumes,
        assets,
        coaVolumes,
        sensors,
        sourceData,
        colors,
        drones,
        visibleDrones,
        uniqueSources,
        mapTileOptions,
        fetchMapData,
        fetchSensors,
        getDrones,
        getUniqueSources,
        externalOperations,
        setExternalOperations
    };

    return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
}
